import React from 'react';
import {
  AppBar,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Pagination,
  Toolbar,
  Typography,
  useMediaQuery,
  type SxProps,
  type Theme
} from '@mui/material';
import { extractApiErrorMessage, notifications } from '../utils';
import { useTranslation } from 'react-i18next';
import { Loader } from '.';
import { LoaderSize } from '../constants/constants';
import CloseIcon from '@mui/icons-material/Close';
import { V4_OUTLINED_DEFAULT, V4_SMALL_SCREEN } from '../theme/v4Compat';
import { ApiService } from '../hooks';
import { MortalityReasonOption, toMortalityReasonOption } from '../models/injection';

interface MortalityReasonSelectModalProps {
  mortalityReason: MortalityReasonOption | null;
  onChange: (supply: MortalityReasonOption | null) => void;
  selectorId?: string;
  disableAny?: boolean;
  disabled?: boolean;
  /** Extra styles for the trigger button. */
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
}

const MortalityReasonSelectModal = React.forwardRef((props: MortalityReasonSelectModalProps, ref) => {
  const { mortalityReason, onChange, selectorId, disableAny, sx, disabled, fullWidth } = props;
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const [mortalityReasons, setMortalityReasons] = React.useState<MortalityReasonOption[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const [text, setText] = React.useState('');
  const apiService = new ApiService();

  React.useImperativeHandle(ref, () => ({
    openModal() {
      setOpen(true);
    }
  }));

  // pagination
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };
  // * pagination

  const displayCount = 10;

  const loadMortalityReasons = async () => {
    setIsBusy(true);

    apiService.api.mortalityReasons
      .mortalityReasonsMortalityReasonsSearch({ Page: page, Size: displayCount })
      .then((result) => {
        setMortalityReasons(result.data.data.map(toMortalityReasonOption));
        setTotal(result.data.total);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleSelectorClick = () => {
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && !loaded) {
      loadMortalityReasons();
    }
  }, [open, loaded]);

  React.useEffect(() => {
    if (!mortalityReason) {
      setTextAny();
    } else {
      setTextByMortalityReason(mortalityReason);
    }
  }, [mortalityReason]);

  const handleClose = () => {
    setOpen(false);
  };

  const setTextByMortalityReason = (mortalityReason: MortalityReasonOption) => {
    const name = mortalityReason?.name?.trim();
    setText(name && name.length > 0 ? name : '-');
  };

  const handleSelectClick = (mortalityReason: MortalityReasonOption) => {
    setTextByMortalityReason(mortalityReason);
    setOpen(false);
    onChange(mortalityReason);
  };

  const setTextAny = () => {
    setText(t('f-mortality-reason-any'));
  };
  const handleSelectAny = () => {
    setOpen(false);
    setTextAny();
    onChange(null);
  };

  return (
    <>
      <Button
        size="medium"
        key={selectorId || 'supplySelectModalBtn'}
        fullWidth={fullWidth}
        variant="outlined"
        color="inherit"
        sx={[V4_OUTLINED_DEFAULT, ...(Array.isArray(sx) ? sx : [sx])]}
        disabled={disabled}
        endIcon={<Icon>{'sick'}</Icon>}
        onClick={handleSelectorClick}
      >
        {text}
      </Button>

      <Dialog
        onClose={handleClose}
        aria-labelledby="mortality-reason-select-title"
        fullWidth
        maxWidth="sm"
        open={open}
        fullScreen={fullScreen}
      >
        {fullScreen ? (
          <AppBar position="relative">
            <Toolbar>
              <div className="flex w-full justify-between">
                <div className="flex items-center">
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="h6">{t('f-mortality-reason-select-mod')}</Typography>
                </div>
              </div>
            </Toolbar>
          </AppBar>
        ) : (
          <DialogTitle id="mortality-reason-select-title">{t('f-mortality-reason-select-mod')}</DialogTitle>
        )}

        <DialogContent sx={{ p: 0 }}>
          <Divider />

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <div className="mr-4 mt-2 flex justify-end">
              <Typography>{t('f-search-results')}</Typography>
              <div className="ml-2">
                <Chip label={total} variant="outlined" size="small" id="totalChip" />
              </div>
            </div>
          )}

          {loaded && mortalityReasons && (
            <List dense>
              {mortalityReasons.map((mortalityReason) => (
                <ListItemButton onClick={() => handleSelectClick(mortalityReason)} key={mortalityReason.id}>
                  <ListItemAvatar>
                    <Avatar>
                      {typeof mortalityReason.name === 'string' && mortalityReason.name.length > 0
                        ? mortalityReason.name[0].toUpperCase()
                        : '-'}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText primary={mortalityReason.name || t('f-com-unnamed')} />
                </ListItemButton>
              ))}

              <ListItem id="pagination-li" sx={{ justifyContent: 'center' }}>
                <Pagination
                  count={Math.floor(total / displayCount)}
                  size="small"
                  color="primary"
                  showFirstButton
                  showLastButton
                  page={page}
                  onChange={handleChangePage}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>

        <DialogActions>
          {!fullScreen && (
            <Button onClick={handleClose} color="primary">
              {t('m-global-close')}
            </Button>
          )}
          <Button onClick={handleSelectAny} color="primary" disabled={disableAny}>
            {t('f-mortality-reason-any')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default MortalityReasonSelectModal;
